import { useEffect, useMemo, useState } from "react";
import Button from "../ui/Button.jsx";
import { useToast } from "../../hooks/useToast.js";
import { mapAdminError } from "../../utils/adminApiErrors.js";
import ReassignmentTargetSelect from "./ReassignmentTargetSelect.jsx";
import { formatName } from "../../utils/adminUserFormat.js";

const inlineAlertStyle = { margin: "0 0 12px", color: "var(--critical)", fontSize: "13px" };

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid var(--border-bright)",
  background: "var(--bg-elevated)",
  color: "var(--text-primary)",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

export default function TransferOwnershipPanel({ actorId, onCancel, onDone }) {
  const { show } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientId, setRecipientId] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function loadRecipients() {
      setLoadingRecipients(true);
      try {
        const res = await fetch("/api/admin/users?include_deleted=false", { credentials: "include" });
        if (!res.ok) return;
        const body = await res.json().catch(() => ({}));
        const users = Array.isArray(body?.users) ? body.users : [];
        const options = users
          .filter((u) => String(u.status || "").toLowerCase() === "active")
          .filter((u) => Boolean(u.is_admin))
          .filter((u) => String(u.id) !== String(actorId))
          .map((u) => ({
            id: String(u.id),
            email: String(u.email || ""),
            label: `${formatName(u.name)} (${u.email || "unknown"})`,
          }));
        if (!cancelled) setRecipients(options);
      } finally {
        if (!cancelled) setLoadingRecipients(false);
      }
    }
    loadRecipients();
    return () => {
      cancelled = true;
    };
  }, [actorId]);

  const selectedRecipient = useMemo(
    () => recipients.find((r) => r.id === recipientId) || null,
    [recipients, recipientId],
  );

  const emailMatches = useMemo(() => {
    if (!selectedRecipient?.email) return false;
    return confirmEmail.trim().toLowerCase() === selectedRecipient.email.trim().toLowerCase();
  }, [confirmEmail, selectedRecipient]);

  const submitDisabled = loading || !recipientId || !emailMatches;

  const emailConfirmError =
    selectedRecipient && confirmEmail.trim() && !emailMatches
      ? "Email must match the selected administrator exactly."
      : null;

  function handleRecipientChange(id) {
    setRecipientId(id);
    setConfirmEmail("");
  }

  async function handleSubmit() {
    if (submitDisabled || !selectedRecipient) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/admin/users/${encodeURIComponent(selectedRecipient.id)}/transfer-ownership`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            confirm_recipient_email: confirmEmail.trim(),
          }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        show(mapAdminError(body?.error), "error");
        return;
      }
      const newEmail = body?.new_owner?.email || selectedRecipient.email;
      show(`Ownership transferred to ${newEmail}. You are now a regular admin.`, "success");
      onDone?.();
    } catch {
      show("Failed to transfer ownership. Try again.", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h3 style={{ margin: "0 0 10px", fontSize: "18px", fontWeight: 600 }}>Transfer ownership</h3>
      <p className="text-secondary" style={{ margin: "0 0 12px", fontSize: "14px", lineHeight: 1.5 }}>
        Transferring ownership gives another admin full administrative control. You will become a regular
        admin. This action is reversible only by the new owner.
      </p>
      <div
        style={{
          marginBottom: "16px",
          padding: "10px 12px",
          borderRadius: "6px",
          border: "1px solid var(--border)",
          background: "var(--bg-base)",
          fontSize: "13px",
          lineHeight: 1.5,
          color: "var(--text-secondary)",
        }}
      >
        After transfer, you will lose owner-level permissions including the ability to perform another
        transfer.
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>
          New owner
        </label>
        {recipients.length === 0 && !loadingRecipients ? (
          <p className="text-secondary" style={{ fontSize: "13px", margin: 0, lineHeight: 1.5 }}>
            No eligible recipients. Active administrators are required for ownership transfer. Promote a user
            to admin first.
          </p>
        ) : (
          <ReassignmentTargetSelect
            options={recipients}
            value={recipientId}
            onChange={handleRecipientChange}
            disabled={loadingRecipients || loading}
            placeholder={loadingRecipients ? "Loading administrators…" : "Select an administrator"}
          />
        )}
      </div>

      {selectedRecipient ? (
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>
            Type the recipient&apos;s email to confirm: {selectedRecipient.email}
          </label>
          <input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            disabled={loading}
            autoComplete="off"
            style={inputStyle}
          />
          {emailConfirmError ? (
            <p role="alert" style={{ ...inlineAlertStyle, marginTop: "8px" }}>
              {emailConfirmError}
            </p>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: "10px" }}>
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleSubmit} loading={loading} disabled={submitDisabled}>
          Transfer ownership
        </Button>
      </div>
    </div>
  );
}
